import { GameState, Player, Judge, StreamInfo, GameRole } from "../types";

/**
 * Maps game role strings to numeric values for the API
 */
const roleToNumber: Record<GameRole, number> = {
  none: 0,
  red: 1,
  black: 2,
  don: 3,
  sheriff: 4,
};

/**
 * Builds a StreamInfo object from current game state for pushing to the backend
 *
 * @param params - Game state parameters
 * @returns StreamInfo object formatted for the pushStream API
 */
export function buildStreamInfo(params: {
  gameState: GameState;
  gamePlayers: Player[];
  gameJudge?: Player;
  gameRoles: GameRole[];
  nominations: Record<string, string>;
  tableNumber: number;
  gameNumber?: number;
  streamBanner?: Record<string, any>;
  overrideGamePhase?: number | null;
}): StreamInfo {
  const {
    gameState,
    gamePlayers,
    gameJudge,
    gameRoles,
    nominations,
    tableNumber,
    gameNumber,
    streamBanner,
    overrideGamePhase,
  } = params;

  // Convert nominations object to array of numbers in speaking order
  // This matches the order used in Voting.tsx
  const nominationsArray: number[] = [];
  const seenCandidates = new Set<number>();

  // Get players in speaking order starting from firstTalk
  for (let i = 0; i < 10; i++) {
    const nominatorIndex = (gameState.firstTalk + i) % 10;
    const nomination = nominations[nominatorIndex.toString()];

    if (nomination) {
      const candidateNum = parseInt(nomination);
      // Only add unique candidates who are still alive
      if (!seenCandidates.has(candidateNum) && !gameState.playersLeaved.includes(candidateNum)) {
        seenCandidates.add(candidateNum);
        nominationsArray.push(candidateNum);
      }
    }
  }

  // Convert vanga array to comma-separated string
  const vangaString = gameState.vanga.join(",");

  // Convert role strings to numbers
  const playerRolesNumbers = gameRoles.map((role) => roleToNumber[role]);

  // Build judge object if judge exists
  const judgeInfo: Judge | undefined = gameJudge
    ? {
        user_id: gameJudge.user_id,
        name: gameJudge.nickname,
        avatar: gameJudge.avatar,
        level: gameJudge.level || 0, // Include judge level
      }
    : undefined;

  // Extract player data from gamePlayers array
  const playerClubs = gamePlayers.map((p) => p.club_name || null);
  const playerClubLogos = gamePlayers.map((p) => p.club_logo || null);
  const playerMasters = gamePlayers.map((p) => {
    // Extract master level from masters string (e.g., "4 ММ" -> 4)
    /*if (p.masters) {
      const match = p.masters.match(/^\d+/);
      return match ? parseInt(match[0]) : null;
    }*/
    return null;
  });
  const playerCities = gamePlayers.map((p) => p.city || null);

  // Player statistics
  const playerStatsGamesRed = gamePlayers.map((p) => p.games_played_red || null);
  const playerStatsGamesBlack = gamePlayers.map((p) => p.games_played_black || null);
  const playerStatsGamesSheriff = gamePlayers.map((p) => p.games_played_sheriff || null);
  const playerStatsGamesDon = gamePlayers.map((p) => p.games_played_don || null);
  const playerStatsWinsRed = gamePlayers.map((p) => p.wins_red || null);
  const playerStatsWinsBlack = gamePlayers.map((p) => p.wins_black || null);
  const playerStatsWinsSheriff = gamePlayers.map((p) => p.wins_sheriff || null);
  const playerStatsWinsDon = gamePlayers.map((p) => p.wins_don || null);

  // Current tournament statistics (all null for now as they're not tracked)
  const playerCurrGamesRed = gamePlayers.map(() => null);
  const playerCurrGamesBlack = gamePlayers.map(() => null);
  const playerCurrGamesSheriff = gamePlayers.map(() => null);
  const playerCurrGamesDon = gamePlayers.map(() => null);
  const playerCurrWinsRed = gamePlayers.map(() => null);
  const playerCurrWinsBlack = gamePlayers.map(() => null);
  const playerCurrWinsSheriff = gamePlayers.map(() => null);
  const playerCurrWinsDon = gamePlayers.map(() => null);

  // Combine playersLeaved and playersLeavingNow for API
  // Players saying farewell are considered "left" for stream display
  const allPlayersLeaved = [
    ...gameState.playersLeaved,
    ...(gameState.playersLeavingNow || []),
  ];

  // For playersLeavedGP, determine correct phase for each player in playersLeavingNow
  const currentPhase = overrideGamePhase !== null && overrideGamePhase !== undefined ? overrideGamePhase : gameState.gamePhase;

  // Check if player was killed at night (last otstrel entry)
  const lastNightKill = gameState.otstrel.length > 0 ? gameState.otstrel[gameState.otstrel.length - 1] : 0;
  const isCurrentPhaseDay = currentPhase % 2 === 0;

  const allPlayersLeavedGP = [
    ...gameState.playersLeavedGP,
    ...(gameState.playersLeavingNow || []).map((playerNum) => {
      // If player was killed at night (in last otstrel) and current phase is day, use previous phase (night)
      if (lastNightKill === playerNum && isCurrentPhaseDay) {
        return currentPhase - 1;
      }
      // Otherwise use current phase (for voted players or other cases)
      return currentPhase;
    }),
  ];

  // Build complete StreamInfo object
  const streamInfo: StreamInfo = {
    taid: tableNumber,
    gamePhase: currentPhase,
    players: gameState.players,
    playerNames: gameState.playerNames,
    playerAvatars: gameState.playerAvatars,
    playerRoles: playerRolesNumbers,
    playersLeaved: allPlayersLeaved,
    playersLeavedPenalty: gameState.playersLeavedPenalty,
    playersLeavedGP: allPlayersLeavedGP,
    playerFauls: gameState.playerFauls,
    otstrel: gameState.otstrel,
    proverkiDon: gameState.proverkiDon,
    proverkiSheriff: gameState.proverkiSheriff,
    vanga: vangaString,
    best_move: gameState.bestMove,
    nominations: nominationsArray,
    pobeda: gameState.pobeda,
    redCnt: gameState.redCnt,
    blackCnt: gameState.blackCnt,
    judge: judgeInfo,
    gNumber: gameNumber !== undefined ? gameNumber : gameState.gnumber,
    part_number: gameState.currPart || 0,
    isFinal: gameState.isFinal,
    // Additional player data
    playerClubs,
    playerClubLogos,
    playerMasters,
    playerCities,
    playerStatsGamesRed,
    playerStatsGamesBlack,
    playerStatsGamesSheriff,
    playerStatsGamesDon,
    playerStatsWinsRed,
    playerStatsWinsBlack,
    playerStatsWinsSheriff,
    playerStatsWinsDon,
    playerCurrGamesRed,
    playerCurrGamesBlack,
    playerCurrGamesSheriff,
    playerCurrGamesDon,
    playerCurrWinsRed,
    playerCurrWinsBlack,
    playerCurrWinsSheriff,
    playerCurrWinsDon,
    banner: streamBanner || {}, // Use provided banner or empty object
  };

  return streamInfo;
}
